// src/pages/Clients/components/ClientUserModal.jsx
import React, { useEffect, useState } from "react";
import { Modal, Form, Input, Select, App, Button, Row, Col } from "antd";
import { useDispatch, useSelector } from "react-redux";
import ReactPhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { PlusOutlined } from "@ant-design/icons";
import { useNavigate, useLocation } from "react-router-dom";
import { useAddNewNavigation } from "../../../hooks/useAddNewNavigation"; //hooks for add new
import { useRestoreDraft } from "../../../hooks/useRestoreDraft"; // hook for add new

import {
  fetchClientUsers,
  upsertClientUser,
  saveClientUserDraft,
  clearClientUserDraft,
} from "../clientsSlice";
import { getDesignationApi, getClientApi } from "../../../apiservice/hrpayroll";
import DesignationFormModal from "../../Designations/components/DesignationFormModal";
import ClientFormModal from "./ClientFormModal";

const { Option } = Select;

const ClientUserModal = ({ open, onClose, edit, authInfo,
  fixedClientId = null,
  disableClientSelect = false,
  hideClientAddNew = false,
  onSuccess,
 }) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { message } = App.useApp();

  const [designations, setDesignations] = useState([]);
  const [designationModalOpen, setDesignationModalOpen] = useState(false);

  const [clients, setClients] = useState([]);
  const [clientModalOpen, setClientModalOpen] = useState(false);
  const [clientsLoaded, setClientsLoaded] = useState(false);

  const { clientUserDraft, shouldReopenClientUserModal } = useSelector(
    (s) => s.clientsSuite
  );

  // 🟢 Auto-restore on returning
  useEffect(() => {
    const query = new URLSearchParams(location.search);
    if (
      query.get("backTo") === "clientUserModal" &&
      shouldReopenClientUserModal
    ) {
      form.setFieldsValue(clientUserDraft || {});
      dispatch(clearClientUserDraft());
      if (!open) {
        // If parent handles modal open externally, notify
     }
    }
  }, [location.search]);

  // Load dropdown data
  useEffect(() => {
    if (open) loadData();
  }, [open]);

  // client reporting manager modal opened from employee page
    useEffect(() => {
      if (
        open &&
        fixedClientId &&
        clientsLoaded &&
        clients.some((c) => c.id === fixedClientId)
      ) {
        form.setFieldsValue({
          client_id: fixedClientId,
        });

        // 🔥 clear validation error if any
        form.validateFields(["client_id"]);
      }
  }, [open, fixedClientId, clientsLoaded, clients]);




  useEffect(() => {
    if (!open) return;
    if (edit) {
      form.setFieldsValue({
        client_user_name: edit.name,
        client_email: edit.email,
        designation: edit.designation,
        client_id: edit.client_id,
        phone: {
          country_code: edit.country_code || authInfo?.country_code || "971",
          contact_no: edit.contact_no || "",
        },
      });
    } else {
      form.resetFields();
      form.setFieldsValue({
        phone: {
          country_code: authInfo?.country_code || "971",
          contact_no: "",
        },
      });
    }
  }, [edit, open]);

  const loadData = async () => {
    try {
      const [desRes, cliRes] = await Promise.all([
        getDesignationApi(),
        getClientApi(),
      ]);
      setDesignations(desRes?.data || []);
      setClients(cliRes?.data || []);
      setClientsLoaded(true);
    } catch (err) {
      console.error("Failed to load dropdowns", err);
    }
  };

  const goToAddNew = useAddNewNavigation();
  useRestoreDraft(form, "clientUserModal");

  const saveDraftAndNavigate = (path) => {
    const currentValues = form.getFieldsValue();
    dispatch(saveClientUserDraft(currentValues));
    navigate(`${path}?from=clientUserModal`);
  };

  const onFinish = async (values) => {
    const phone = values.phone || {
      country_code: authInfo?.country_code || "971",
      contact_no: "",
    };

    const payload = {
      id: edit?.id || 0,
      client_user_name: values.client_user_name?.trim(),
      email: values.client_email?.trim(),
      country_code: phone.country_code,
      contact_no: phone.contact_no,
      designation_id: values.designation,
      client_id: fixedClientId || values.client_id,
      action: edit ? "update" : "create",
    };

    try {
      const res = await dispatch(upsertClientUser(payload)).unwrap();
      message.success(res?.msg || "Client user saved");
      dispatch(fetchClientUsers());

      // 🔥 notify parent
      onSuccess?.({
        id: res?.id,
        name: values.client_user_name,
        client_id: values.client_id,
      });

      onClose();
      dispatch(clearClientUserDraft());
    } catch (err) {
      message.error(err?.msg || "Failed to save client user");
    }
  };

  return (
    <>
    <Modal
      open={open}
      title={edit ? "Edit Client User" : "New Client User"}
      onCancel={onClose}
      onOk={() => form.submit()}
      okText={edit ? "Save" : "Create"}
      destroyOnClose
      width={600}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        requiredMark={(label, { required }) =>
          required ? (
            <>
              {label} <span style={{ color: "#ff4d4f" }}>*</span>
            </>
          ) : (
            label
          )
        }
      >
        <Form.Item
          label="Client User Name"
          name="client_user_name"
          rules={[{ required: true, message: "Client User Name is required" }]}
        >
          <Input placeholder="Enter name" maxLength={50} />
        </Form.Item>

        <Form.Item
          label="Email"
          name="client_email"
          rules={[
            { required: true, message: "Email is required" },
            { type: "email", message: "Invalid email format" },
          ]}
        >
          <Input placeholder="Enter email" maxLength={50} />
        </Form.Item>

        <Form.Item
          label="Phone"
          name="phone"
          rules={[
            {
              validator: (_, value) => {
                if (!value || !value.contact_no) {
                  return Promise.reject(new Error("Phone is required"));
                }
                return Promise.resolve();
              },
            },
          ]}
          getValueProps={(value) => {
            const country_code =
              value?.country_code || authInfo?.country_code || "971";
            const contact_no = value?.contact_no || "";
            return { value: `+${country_code} ${contact_no}` };
          }}
          getValueFromEvent={(value, country) => {
            const str = typeof value === "string" ? value : "";
            const dial = country?.dialCode || authInfo?.country_code || "971";
            let phoneNumber = str.replace(`+${dial}`, "").trim();
            if (phoneNumber.startsWith(dial))
              phoneNumber = phoneNumber.slice(dial.length);
            return { country_code: dial, contact_no: phoneNumber };
          }}
        >
          <ReactPhoneInput
            country="us"
            inputStyle={{ width: "100%" }}
            enableSearch
          />
        </Form.Item>

        {/* Designation Field with Add New */}
        <Form.Item
          label={
            <Row justify="space-between" align="middle">
              <Col>Designation</Col>
              <Col>
                <Button
                  type="link"
                  size="small"
                  icon={<PlusOutlined />}
                  onClick={() => setDesignationModalOpen(true)}
                  style={{ color: "#1677ff", cursor: "pointer" }}
                >
                  Add New
                </Button>
              </Col>
            </Row>
          }
          name="designation"
          rules={[{ required: true, message: "Designation is required" }]}
        >
          <Select placeholder="Select Designation" showSearch>
            {designations.map((d) => (
              <Option key={d.id} value={d.id}>
                {d.name}
              </Option>
            ))}
          </Select>
        </Form.Item>

        {/* Client Field with Add New */}
        <Form.Item
          label={
            <Row justify="space-between" align="middle">
              <Col>Client</Col>
             {!hideClientAddNew &&( 
              <Col>
                <Button
                  type="link"
                  size="small"
                  icon={<PlusOutlined />}
                  onClick={() => setClientModalOpen(true)}
                >
                  Add New
                </Button>

              </Col>
             )}
            </Row>
          }
          name="client_id"
          rules={[{ required: true, message: "Client is required" }]}
        >
          <Select placeholder="Select Client" showSearch disabled={disableClientSelect}>
            {clients.map((c) => (
              <Option key={c.id} value={c.id}>
                {c.name}
              </Option>
            ))}
          </Select>
        </Form.Item>
      </Form>
    </Modal>
    
    {/* Designation Modal for add new designation button */}
    <DesignationFormModal
    open={designationModalOpen}
    onClose={() => setDesignationModalOpen(false)}
    onSuccess={async () => {
    // 🔁 Reload fresh data with real IDs
    const res = await getDesignationApi();
    setDesignations(res?.data || []);

    // (optional) auto-select last inserted
    const last = res?.data?.[0];
    if (last?.id) {
      form.setFieldsValue({ designation: last.id });
    }
    }}
    />
    {/* Client modal for add new client button */}
    <ClientFormModal
    open={clientModalOpen}
    onClose={() => setClientModalOpen(false)}
    authInfo={authInfo}
    onSuccess={async () => {
      // 🔁 Reload clients
      const res = await getClientApi();
      setClients(res?.data || []);

      // ✅ Auto-select newly added client
      const last = res?.data?.[0];
      if (last?.id) {
        form.setFieldsValue({ client_id: last.id });
      }
    }}
    />

</>
  );
};

export default ClientUserModal;
